/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useRef, useEffect, useState, JSX } from 'react';
import { MultiSelectComponent } from '@syncfusion/ej2-react-dropdowns';
import { debounce } from '@syncfusion/ej2-base';
import { Query } from '@syncfusion/ej2-data';
import { withTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { FormInputContainerStyled, FormInputLabelContainerStyled, ValidationLabelStyled } from './styles';
import { setFieldLabel } from './utilis/setFieldLabel';
import Handlebars from 'handlebars';
import parseHTML from 'html-react-parser';
import { toast } from 'src/utils/notification';


interface TemplateInput {
  item?: string;
  header?: string;
  value?: string;
}

interface TemplateProps {
  [key: string]: any;
  template?: TemplateInput | string;
  label?: string;
}

const SelectField: FC<any> = ({ field, isLocked, options, t, setNewValue, renderData }) => {
  const allowMultiSelection = field.type === 'multiSelect';
  const dropdownInstanceRef = useRef(null);
  const [selectOptions, setSelectOptions] = useState(options);
  const data = options;

  const [selectedValue, setSelectedValue] = useState(null);

  useEffect(() => {
    if (!isLocked) {
      setSelectedValue(fillValueToSelects(options));
    }
  }, [isLocked, options]);

  const onFiltering: any = debounce((e) => {
    let query = new Query();
    query = (e.text !== '') ? query.where('label', 'contains', e.text, true) : query;
    e.updateData(data, query);
  }, 50);

  const onSelect = (args) => {
    const { itemData } = args;
    const { id } = itemData;

    const updatedOptions = selectOptions.map(option => ({
      ...option,
      selected: allowMultiSelection ? option.id === id ? !option.selected : option.selected : option.id === id,
    }));

    if (!allowMultiSelection) {
      dropdownInstanceRef.current.clear();
    }

    new Promise(resolve => {
      setSelectOptions(updatedOptions);
      resolve(true);
    }).then(() => {
      setNewValue(updatedOptions);
    });
  };

  const onRemoved = (args) => {
    const { itemData } = args;
    const { id } = itemData;

    const updatedOptions = selectOptions.map(option => ({
      ...option,
      selected: allowMultiSelection ? option.selected && option.id !== id : false,
    }));

    new Promise(resolve => {
      setSelectOptions(updatedOptions);
      resolve(true);
    }).then(() => {
      setNewValue(updatedOptions);
    });
  };

  const renderPlaceholder = () => {
    if (options) {
      const selectedOption = options.find(o => o.selected === true);
      if (selectedOption) {
        return selectedOption.label;
      } else if (options.length === 1) {
        return options[0].label;
      } else if (!isLocked) {
        return 'Select something';
      }
    }
    return null;
  };

  const fillValueToSelects = (dataArray) => {
    if (!Array.isArray(dataArray)) {
      return null;
    }
    return dataArray.filter(obj => obj.selected).map(obj => obj.id ?? 0);
  };

  const parseTemplates = (input?: string | TemplateInput): Required<TemplateInput> => {
    try {
      const { item = '', header = '', value = '' } =
        typeof input === 'string' ? JSON.parse(input) : input ?? {};
      return { item, header, value };
    } catch (e) {
      return { item: '', header: '', value: '' };
    }
  };

  // Template-RENDERER
  const renderTemplate = (raw: string, props: TemplateProps, className: string): JSX.Element | null => {
    try {
      if (raw && typeof raw === 'string') {
        const compiled = Handlebars.compile(raw);
        const html = compiled(props);
        const sanitized = DOMPurify.sanitize(html);
        return <div className={className}>{parseHTML(sanitized)}</div>;
      }
    } catch (err) {
      toast.error('Field.template', 'Template can\'t be rendered');
    }
    return null;
  };

  const templates = parseTemplates(field.template);

  const headerTemplate = (props: TemplateProps) =>
    renderTemplate(templates.header, props, 'multiselect-header');

  const itemTemplate = (props: TemplateProps) => {
    const template = renderTemplate(templates.item, props, 'multiselect-item') ?? <span>{props.label}</span>;
    return template;
  };

  const valueTemplate = (props: TemplateProps) =>
    renderTemplate(templates.value, props, 'multiselect-value') ?? <span>{props.label}</span>;


  if (options) {
    options.forEach(option => {
      option.label = option.label ?? option.name;
      option.id = option.id ?? option.name;
    });
  }

  return (
    <FormInputContainerStyled hidden={+field.hidden}>
      <FormInputLabelContainerStyled>
        {setFieldLabel(field, renderData, selectedValue, t)}
      </FormInputLabelContainerStyled>
      <ValidationLabelStyled id={`validation-label-${field.attribute.key}`}></ValidationLabelStyled>
      <div className='form-input-element control-styles'>
        <MultiSelectComponent
          dataSource={data}
          ref={dropdownInstanceRef}
          className='form-input-element'
          placeholder={renderPlaceholder()}
          value={isLocked ? selectedValue : fillValueToSelects(options)}
          select={onSelect}
          removed={onRemoved}
          enabled={!isLocked}
          filtering={onFiltering}
          allowFiltering={true}
          fields={{ groupBy: 'groupBy', text: 'label', value: 'id' }}
          itemTemplate={itemTemplate}
          headerTemplate={headerTemplate}
          valueTemplate={valueTemplate}
          mode='Box'
          name={field.name}
          data-msg-containerid={`error-${field.attribute.key}`}
        />
      </div>
      <div id={`error-${field.attribute.key}`} />
    </FormInputContainerStyled>
  );
};

export default withTranslation()(SelectField);
